import {connect} from 'react-redux';

import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {LinkedConversationStatusesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {AppState} from '../../../../store/appState';
import {
  makeGetReplyChannels,
  makeGetSortedComposeChannels,
} from '../../../../store/data/channels/channelsSelectors';
import {makeGetConversation} from '../../../../store/data/conversations/conversationsSelectors';
import {getSortedSignatures} from '../../../../store/data/signatures/signaturesSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {PickChannelDropdown} from './pickChannelDropdown';

/*
 * Props.
 */

export interface SaveChannelOptions {
  newChannel: ChannelModel;
  shouldResetContent: boolean;
}

export type ChannelSaver = (options: SaveChannelOptions) => void;

interface ChannelPickerDropdownContainerProps {
  message: DraftMessageModel;
  channel: ChannelModel;
  saveChannel: ChannelSaver;
}

/*
 * Selectors.
 */

function getMessage(_state: AppState, props: ChannelPickerDropdownContainerProps) {
  return props.message;
}

function getMessageConversationId(_state: AppState, props: ChannelPickerDropdownContainerProps) {
  return props.message.conversationId;
}

function getChannel(_state: AppState, props: ChannelPickerDropdownContainerProps) {
  return props.channel;
}

function makeGetIsConversationLinked() {
  const getConversation = makeGetConversation(getMessageConversationId);
  return createSelector(
    [getConversation],
    (conversation) => conversation?.linkStatus === LinkedConversationStatusesEnum.LINKED,
  );
}

function makeGetAlternativeChannels() {
  const getComposeChannels = makeGetSortedComposeChannels({});
  const getReplyChannels = makeGetReplyChannels({getMessage});

  return (state: AppState, props: ChannelPickerDropdownContainerProps) => {
    // If this is a reply, return the reply channels.
    const message = getMessage(state, props);
    if (message.inReplyToId) {
      return getReplyChannels(state, props);
    }

    // Otherwise, return the compose channels.
    return getComposeChannels(state, props);
  };
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ChannelPickerDropdownContainerProps>()(() => ({
  channel: getChannel,
  signatures: getSortedSignatures,
  userNamespace: getUserNamespace,
  alternativeChannels: makeGetAlternativeChannels(),
  isConversationLinked: makeGetIsConversationLinked(),
}));

export const PickChannelDropdownContainer = connect(
  mapStateToProps,
  null,
  (stateProps, _dispatchProps, ownProps) => {
    const {channel, saveChannel} = ownProps;

    const onChannelSelected = (newChannel: ChannelModel) => {
      const shouldResetContent = channel.messageType !== newChannel.messageType;
      saveChannel({newChannel, shouldResetContent});
    };

    return {...stateProps, ...ownProps, onChannelSelected};
  },
)(PickChannelDropdown);
